import { GAME_TYPES, GAME_TYPE_LABELS, type GameType } from '@game-keys/shared'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { ArtworkEditor } from '../components/ArtworkEditor'
import { Select } from '../components/Select'
import { StoreLinkEditor } from '../components/StoreLinkEditor'
import { useGameDetail, useResyncGame, useUpdateGame } from '../lib/admin-api'

export const Route = createFileRoute('/admin/game/$id')({
  component: EditGamePage,
})

function EditGamePage() {
  const { id } = Route.useParams()
  const game = useGameDetail(id)

  if (game.isPending) return <p className="text-sm text-ink-muted">Loading…</p>

  if (game.isError || !game.data) {
    return (
      <div>
        <p className="text-sm text-bad">That game could not be loaded.</p>
        <Link to="/admin/library" className="mt-3 inline-block text-sm text-accent">
          Back to the library
        </Link>
      </div>
    )
  }

  return <EditGameForm detail={game.data} gameId={id} />
}

type Detail = NonNullable<ReturnType<typeof useGameDetail>['data']>

/** The editable fields of a game, as the strings a form holds. */
function toForm(detail: Detail) {
  return {
    title: detail.title,
    type: detail.type as GameType,
    summary: detail.summary ?? '',
    releaseDate: detail.releaseDate ?? '',
    developer: detail.developer ?? '',
    publisher: detail.publisher ?? '',
    steamAppId: detail.steamAppId?.toString() ?? '',
    igdbId: detail.igdbId?.toString() ?? '',
    genres: detail.genres.join(', '),
    isHidden: detail.isHidden,
  }
}

function EditGameForm({ detail, gameId }: { detail: Detail; gameId: string }) {
  const update = useUpdateGame(gameId)
  const resync = useResyncGame(gameId)

  const [form, setForm] = useState(() => toForm(detail))
  const [saved, setSaved] = useState(false)

  /**
   * What the server last told us this game was.
   *
   * Compared by value rather than by object identity, because every refetch
   * hands back a fresh object: uploading artwork invalidates this query, and
   * re-seeding on that would wipe whatever was half-typed in the form.
   */
  const serverState = JSON.stringify(toForm(detail))
  const lastSeeded = useRef(serverState)

  // A resync does rewrite the record underneath the form, and that should pull
  // the new values in rather than leave stale text in the inputs.
  useEffect(() => {
    if (lastSeeded.current === serverState) return

    lastSeeded.current = serverState
    setForm(JSON.parse(serverState) as ReturnType<typeof toForm>)
  }, [serverState])

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    setSaved(false)

    update.mutate(
      {
        title: form.title.trim(),
        type: form.type,
        summary: form.summary.trim() || null,
        releaseDate: form.releaseDate || null,
        developer: form.developer.trim() || null,
        publisher: form.publisher.trim() || null,
        steamAppId: form.steamAppId ? Number(form.steamAppId) : null,
        igdbId: form.igdbId ? Number(form.igdbId) : null,
        genreNames: form.genres
          .split(',')
          .map((name) => name.trim())
          .filter(Boolean),
        isHidden: form.isHidden,
      },
      { onSuccess: () => setSaved(true) },
    )
  }

  return (
    <div className="mx-auto max-w-4xl">
      <Link to="/admin/library" className="text-sm text-ink-soft transition-colors hover:text-ink">
        ← Library
      </Link>

      <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">{detail.title}</h1>
          <p className="mt-1 text-sm text-ink-soft">
            <Link
              to="/game/$slug"
              params={{ slug: detail.slug }}
              className="transition-colors hover:text-accent"
            >
              View on the public site →
            </Link>
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={resync.isPending || !detail.steamAppId}
            title={detail.steamAppId ? undefined : 'No Steam appid on this game'}
            onClick={() => resync.mutate('steam')}
            className="rounded-lg border border-line px-3 py-1.5 text-xs text-ink-soft transition-colors hover:border-accent hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
          >
            {resync.isPending ? 'Syncing…' : 'Resync from Steam'}
          </button>
          <button
            type="button"
            disabled={resync.isPending || !detail.igdbId}
            title={detail.igdbId ? undefined : 'No IGDB id on this game'}
            onClick={() => resync.mutate('igdb')}
            className="rounded-lg border border-line px-3 py-1.5 text-xs text-ink-soft transition-colors hover:border-accent hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
          >
            Resync from IGDB
          </button>
        </div>
      </div>

      {resync.isError ? (
        <p role="alert" className="mt-4 rounded-lg border border-bad/30 bg-bad/10 px-4 py-2.5 text-sm text-bad">
          {(resync.error as Error).message}
        </p>
      ) : null}
      {resync.isSuccess ? (
        <p role="status" className="mt-4 rounded-lg border border-good/30 bg-good/10 px-4 py-2.5 text-sm text-good">
          Metadata refreshed.{' '}
          {resync.data.artworkStored > 0
            ? `${resync.data.artworkStored} new ${resync.data.artworkStored === 1 ? 'image' : 'images'} stored.`
            : 'Artwork was already up to date.'}
        </p>
      ) : null}

      <form onSubmit={submit} className="mt-6 rounded-xl border border-line bg-surface-1 p-6">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Title" htmlFor="title" className="sm:col-span-2">
            <Input id="title" value={form.title} onChange={(v) => set('title', v)} />
          </Field>

          <Field label="Type" htmlFor="type">
            <Select
              id="type"
              value={form.type}
              onChange={(value) => set('type', value as GameType)}
              label="Type"
              options={GAME_TYPES.map((value) => ({ value, label: GAME_TYPE_LABELS[value] }))}
            />
          </Field>

          <Field label="Release date" htmlFor="release">
            <Input
              id="release"
              type="date"
              value={form.releaseDate}
              onChange={(v) => set('releaseDate', v)}
            />
          </Field>

          <Field label="Developer" htmlFor="developer">
            <Input id="developer" value={form.developer} onChange={(v) => set('developer', v)} />
          </Field>

          <Field label="Publisher" htmlFor="publisher">
            <Input id="publisher" value={form.publisher} onChange={(v) => set('publisher', v)} />
          </Field>

          <Field label="Steam appid" htmlFor="steam">
            <Input
              id="steam"
              inputMode="numeric"
              value={form.steamAppId}
              onChange={(v) => set('steamAppId', v.replace(/\D/g, ''))}
            />
          </Field>

          <Field label="IGDB id" htmlFor="igdb">
            <Input
              id="igdb"
              inputMode="numeric"
              value={form.igdbId}
              onChange={(v) => set('igdbId', v.replace(/\D/g, ''))}
            />
          </Field>

          <Field label="Genres" htmlFor="genres" className="sm:col-span-2">
            <Input id="genres" value={form.genres} onChange={(v) => set('genres', v)} />
            <p className="mt-1.5 text-xs text-ink-muted">
              Comma-separated. These are the catalog's filters — a game with none never turns up
              under any of them.
            </p>
          </Field>

          <Field label="Description" htmlFor="summary" className="sm:col-span-2">
            <textarea
              id="summary"
              value={form.summary}
              onChange={(event) => set('summary', event.target.value)}
              rows={4}
              className="w-full resize-y rounded-lg border border-line bg-surface-0 px-3 py-2 text-sm text-ink outline-none focus:border-accent"
            />
          </Field>
        </div>

        <label className="mt-5 flex cursor-pointer items-center gap-2 text-sm text-ink-soft">
          <input
            type="checkbox"
            checked={form.isHidden}
            onChange={(event) => set('isHidden', event.target.checked)}
            className="check"
          />
          Hide from the public catalog, even when keys are available
        </label>

        {update.isError ? (
          <p role="alert" className="mt-4 text-sm text-bad">
            {(update.error as Error).message}
          </p>
        ) : null}

        <div className="mt-6 flex items-center gap-3">
          <button
            type="submit"
            disabled={update.isPending || !form.title.trim()}
            className="rounded-lg bg-accent px-4 py-2.5 text-sm font-semibold text-surface-0 transition-colors hover:bg-accent-strong disabled:opacity-40"
          >
            {update.isPending ? 'Saving…' : 'Save changes'}
          </button>
          {saved && !update.isPending ? (
            <span role="status" className="text-sm text-good">
              Saved.
            </span>
          ) : null}
        </div>
      </form>

      <h2 className="mt-8 font-display text-lg font-semibold tracking-tight">Store pages</h2>
      <p className="mt-1 text-sm text-ink-soft">
        Where to go and buy it, shown under the game on the public site. Providers fill these in;
        this is where a wrong one gets fixed, or a storefront neither of them knows about gets
        added.
      </p>

      <StoreLinkEditor gameId={gameId} current={detail.storeLinks} />

      <h2 className="mt-8 font-display text-lg font-semibold tracking-tight">Artwork</h2>
      <p className="mt-1 text-sm text-ink-soft">
        Stored here once set, so the page never depends on someone else's server.
      </p>

      {/* items-start so the portrait card keeps its own height rather than
          stretching to match the two stacked wide ones. */}
      <div className="mt-4 grid items-start gap-4 lg:grid-cols-[19rem_1fr]">
        <ArtworkEditor
          gameId={gameId}
          slot="capsule"
          currentUrl={detail.capsuleUrl}
          aspect="portrait"
        />
        <div className="space-y-4">
          <ArtworkEditor gameId={gameId} slot="hero" currentUrl={detail.heroUrl} aspect="wide" />
          <ArtworkEditor gameId={gameId} slot="logo" currentUrl={detail.logoUrl} aspect="wide" />
        </div>
      </div>
    </div>
  )
}

function Field({
  label,
  htmlFor,
  children,
  className = '',
}: {
  label: string
  htmlFor: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className="mb-2 block text-xs font-medium text-ink-soft">
        {label}
      </label>
      {children}
    </div>
  )
}

function Input({
  id,
  value,
  onChange,
  type = 'text',
  inputMode,
}: {
  id: string
  value: string
  onChange: (value: string) => void
  type?: string
  inputMode?: 'numeric'
}) {
  return (
    <input
      id={id}
      type={type}
      inputMode={inputMode}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={`w-full rounded-lg border border-line bg-surface-0 px-3 py-2 text-sm text-ink outline-none focus:border-accent ${
        type === 'date' ? 'field-date' : ''
      }`}
    />
  )
}
